import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ListingCard from '../components/ListingCard';
import { getListings } from '../services/listingService';
import '../styles/Home.css';

const Home = () => {
    const { user } = useAuth();
    const [featuredListings, setFeaturedListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchLocation, setSearchLocation] = useState('');

    useEffect(() => {
        loadFeaturedListings();
    }, []);

    const loadFeaturedListings = async () => {
        try {
            setLoading(true);
            const listings = await getListings();
            // Get the first 6 listings as featured
            const featured = listings.slice(0, 6);
            setFeaturedListings(featured);
        } catch (error) {
            console.error('Error loading featured listings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        // Navigate to listings page with search parameters
        const params = new URLSearchParams();
        if (searchQuery) params.append('q', searchQuery);
        if (searchLocation) params.append('location', searchLocation);
        
        const searchUrl = `/listings${params.toString() ? '?' + params.toString() : ''}`;
        window.location.href = searchUrl;
    };

    const handleDeleteListing = (listingId) => {
        setFeaturedListings(prev => prev.filter(listing => listing.id !== listingId));
    };

    return (
        <div className="home-page">
            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    <h1 className="hero-title">
                        Buy and Sell Locally
                        <span className="hero-subtitle">Connect with your community</span>
                    </h1>
                    <p className="hero-description">
                        Discover amazing deals from trusted local sellers. 
                        Sell your items quickly and safely in your neighborhood.
                    </p>
                    
                    {/* Search Bar */}
                    <form className="search-form" onSubmit={handleSearch}>
                        <div className="search-inputs">
                            <input
                                type="text"
                                placeholder="What are you looking for?"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input"
                            />
                            <input
                                type="text"
                                placeholder="Location (optional)"
                                value={searchLocation}
                                onChange={(e) => setSearchLocation(e.target.value)}
                                className="search-input location-input"
                            />
                            <button type="submit" className="search-button">
                                Search
                            </button>
                        </div>
                    </form>

                    {/* Quick Actions */}
                    <div className="hero-actions">
                        <Link to="/listings" className="hero-btn primary">
                            Browse All Listings
                        </Link>
                        {user ? (
                            <Link to="/listings/create" className="hero-btn secondary">
                                Sell Something
                            </Link>
                        ) : (
                            <Link to="/register" className="hero-btn secondary">
                                Join Now
                            </Link>
                        )}
                    </div>
                </div>
                
                <div className="hero-image">
                    <div className="hero-illustration">
                        <div className="floating-card card-1">
                            <span className="card-icon">📱</span>
                            <span className="card-text">Local Deals</span>
                        </div>
                        <div className="floating-card card-2">
                            <span className="card-icon">🤝</span>
                            <span className="card-text">Trusted Sellers</span>
                        </div>
                        <div className="floating-card card-3">
                            <span className="card-icon">🚀</span>
                            <span className="card-text">Quick & Easy</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section">
                <div className="container">
                    <h2 className="section-title">Why Choose Local Selling?</h2>
                    <div className="features-grid">
                        <div className="feature-card">
                            <div className="feature-icon">🏠</div>
                            <h3>Local Community</h3>
                            <p>Connect with buyers and sellers in your neighborhood. Build trust through local relationships.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">💰</div>
                            <h3>Better Deals</h3>
                            <p>Find great prices on items you need and sell your unused items for cash.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">🚚</div>
                            <h3>Easy Pickup</h3>
                            <p>Meet locally for quick and convenient transactions. No shipping hassles.</p>
                        </div>
                        <div className="feature-card">
                            <div className="feature-icon">✅</div>
                            <h3>Verified Users</h3>
                            <p>Our verification system helps ensure safe and trustworthy transactions.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Listings Section */}
            <section className="featured-section">
                <div className="container">
                    <div className="section-header">
                        <h2 className="section-title">Featured Listings</h2>
                        <Link to="/listings" className="view-all-link">
                            View All Listings →
                        </Link>
                    </div>
                    
                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Loading featured listings...</p>
                        </div>
                    ) : featuredListings.length > 0 ? (
                        <div className="listings-grid">
                            {featuredListings.map((listing) => (
                                <ListingCard
                                    key={listing.id}
                                    listing={listing}
                                    onDelete={handleDeleteListing}
                                    isOwner={user && listing.user && listing.user.id === user.id}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-icon">📦</div>
                            <h3>No listings yet</h3>
                            <p>Be the first to post a listing in your area!</p>
                            {user ? (
                                <Link to="/listings/create" className="cta-button">
                                    Create Your First Listing
                                </Link>
                            ) : (
                                <Link to="/register" className="cta-button">
                                    Join and Start Selling
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to Start Selling?</h2>
                        <p>Join thousands of users who are already buying and selling locally.</p>
                        <div className="cta-buttons">
                            {user ? (
                                <>
                                    <Link to="/listings/create" className="cta-btn primary">
                                        Post Your First Listing
                                    </Link>
                                    <Link to="/listings" className="cta-btn secondary">
                                        Browse Listings
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link to="/register" className="cta-btn primary">
                                        Get Started Free
                                    </Link>
                                    <Link to="/login" className="cta-btn secondary">
                                        Sign In
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section">
                <div className="container">
                    <div className="stats-grid">
                        <div className="stat-item">
                            <div className="stat-number">1000+</div>
                            <div className="stat-label">Active Users</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">500+</div>
                            <div className="stat-label">Listings Posted</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">95%</div>
                            <div className="stat-label">Satisfaction Rate</div>
                        </div>
                        <div className="stat-item">
                            <div className="stat-number">24h</div>
                            <div className="stat-label">Average Response</div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home; 